import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useLocation,
  useParams,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { useForm, Control } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  ValidatorsProps,
  ajvResolver,
  ExchangeSidepanel,
  BaseellaStatus,
  PermissionEnum,
  responseErrorsResolver,
  ResponseError,
  usePrevious,
  Account,
  Response,
  BaseTransaction,
  formatBalance,
  formatDate,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearSignaturesState } from '../../store/signature/slice';
import { clearStatusesState } from '../../store/statuses/slice';
import { setCollapseAllRows } from './store/slices/list';
import {
  changePanelState,
  clearExchangeTransactionState,
  setCalculatedResult,
} from './store/slices/transaction';
import StatusesUrl from '../../types/statuses-url';
import {
  useGetExchangeTransactionValidatorsQuery,
  useLazyGetExchangeAccountsCategoriesQuery,
  useLazyGetExchangeAccountsListQuery,
  useLazyGetAllFromAccountBalancesQuery,
  useLazyGetAllToAccountBalancesQuery,
  useAddMissingMutation,
} from './store/services';
import {
  clearState,
  clearExpandedGeneralLedgerTransactionState,
} from '../transactions/store/slice';
import { useLazyGetTransactionsAccountBalanceQuery } from '../../store/dictionaries/services';
import { CreateExchangeTransactionFormValues } from './types';
import { MfaToken, MfaType } from '../../store/types';
import useBaseRequests from '../transactions/hooks/use-base-requests';
import useRequests from './hooks/use-requests';

import ROUTES from '../../routes';

const exchangeDateOfRateValue = new Date();

exchangeDateOfRateValue.setHours(new Date().getHours());
exchangeDateOfRateValue.setMinutes(new Date().getMinutes());

const defaultFormValues: any = {
  baseRate: '',
  calculatedAmount: '',
  exchangeActualRate: '',
  exchangeCurrencyFromId: '',
  exchangeCurrencyToId: '',
  exchangeDateOfRate: exchangeDateOfRateValue.toISOString(),
  exchangeMarginPercent: '',
  exchangeProfit: '',
  exchangeRateSourceId: undefined,
  exchangeType: 'SELL',
  recipientAccountNumber: undefined,
  senderAccountNumber: undefined,
  sum: '',
  sellSum: '',
  buySum: '',
  transferDate: '',
  depositDateOfDestinationAccount: '',
};

const formatFormDate = (date: string) => {
  const dateFormatted = new Date(date);

  dateFormatted.setHours(0);
  dateFormatted.setMinutes(0);
  dateFormatted.setSeconds(0);
  dateFormatted.setMilliseconds(0);

  return dateFormatted;
};

const ExchangeTransaction = ({
  withoutCorrections,
}: {
  // eslint-disable-next-line react/require-default-props
  withoutCorrections?: boolean;
}) => {
  useGetExchangeTransactionValidatorsQuery();

  const {
    exchange: {
      transaction: {
        panelIsOpen,
        validators,
        transaction,
        calculateResult,
        accountCategories,
        glTransactions,
        fromAccountBalances,
        toAccountBalances,
        syncedTransactions,
        integrationLogs,
        integrationLogsPagination,
      },
      list: { selectedTake },
    },
    transactionsDictionaries: {
      currencies,
      activePermissions,
      rateSources,
      baseRateSettings,
      currenciesList,
      paymentTypes,
      overdraftEnabled,
    },
    transactions: {
      currentEmployee,
      chats,
      companyOfficials,
      document: documentItem,
      documents,
      expandedGLTransaction,
      expandedGLTransactionCreditBalance,
      expandedGLTransactionDebitBalance,
      transactionChangeLogs,
      transactionChangeLogsPagination,
      transactionComments,
      transactionCommentsPagination,
    },
    statuses: { statuses, statusLogs },
    signatures: { signatures },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { ffSettings } = useAppSelector((state) => state.root);

  const { sendMessage } = useOutletContext<{ sendMessage: SendMessage }>();

  const [fromAccountBalance, setFromAccountBalance] = useState<
    number | undefined
  >();
  const [toAccountBalance, setToAccountBalance] = useState<
    number | undefined
  >();
  const [senderAccounts, setSenderAccounts] = useState<Account[]>([]);
  const [recipientAccounts, setRecipientAccounts] = useState<Account[]>([]);
  const [createdTransactionId, setCreatedTransactionId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [statusChangeError, setStatusChangeError] = useState('');
  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);
  const [backPath, setBackPath] = useState('');
  const [defaultCalculateResult, setDefaultCalculateResult] = useState<{
    exchangeActualRate?: number;
    exchangeMarginPercent?: number;
    exchangeDateOfRate?: string;
    inverseActualRate?: number;
  }>({});
  const [calculatedBodyValues, setCalculatedBodyValues] = useState<any>();
  const [calculatedType, setCalculatedType] = useState<
    'rate' | 'margin' | 'date' | 'inversed-rate'
  >();
  const [inverseRateChanged, setInverseRateChanged] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { transactionId } = useParams();
  const { search, state: locationState } = useLocation();

  const {
    control,
    formState: { dirtyFields, errors },
    reset,
    setError,
    setValue,
    watch,
    clearErrors,
    getValues,
  } = useForm({
    resolver: ajvResolver(
      validators?.createExchangeTransactionSchema as ValidatorsProps,
    ),
    defaultValues: defaultFormValues,
  });

  const fields = watch();
  const sender = watch('senderAccountNumber');
  const recipient = watch('recipientAccountNumber');
  const currencyFrom = watch('exchangeCurrencyFromId');
  const currencyTo = watch('exchangeCurrencyToId');
  const exchangeType = watch('exchangeType');
  const exchangeDateOfRate = watch('exchangeDateOfRate');
  const exchangeCurrencyFixedId = watch('exchangeCurrencyFixedId');
  const exchangeActualRate = watch('exchangeActualRate');
  const exchangeMarginPercent = watch('exchangeMarginPercent');
  const transferDateValue = watch('transferDate');
  const providersRate = watch('providersRate');
  const sellSum = watch('sellSum');
  const buySum = watch('buySum');
  const rateSource = watch('exchangeRateSourceId');
  const inverseRate = watch('inverseActualRate');

  const prevExchangeDateOfRate = usePrevious(exchangeDateOfRate);
  const prevExchangeActualRate = usePrevious(exchangeActualRate);
  const prevExchangeMarginPercent = usePrevious(exchangeMarginPercent);
  const prevSender = usePrevious(sender);
  const prevRecipient = usePrevious(recipient);
  const prevSellSum = usePrevious(sellSum);
  const prevBuySum = usePrevious(buySum);
  const prevExchangeType = usePrevious(exchangeType);
  const prevCalculatedAmount = usePrevious(calculateResult?.calculatedAmount);
  const prevCurrencyFrom = usePrevious(currencyFrom);
  const prevCurrencyTo = usePrevious(currencyTo);
  const prevRateSource = usePrevious(rateSource);
  const prevInversedRate = usePrevious(inverseRate);
  const prevFromAccountBalances = usePrevious(fromAccountBalances);
  const prevToAccountBalances = usePrevious(toAccountBalances);

  const isDirty =
    Object.keys(dirtyFields).length !== 0 || locationState?.duplicate;

  const [addMissing, { isLoading: addMissingInProgress }] =
    useAddMissingMutation();

  const [
    getExchangeAccountsCategories,
    { isLoading: accountsCategoriesLoading },
  ] = useLazyGetExchangeAccountsCategoriesQuery();

  const [
    getFromAccountBalance,
    {
      isLoading: fromAccountBalanceIsLoading,
      isFetching: fromAccountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();

  const [
    getToAccountBalance,
    {
      isLoading: toAccountBalanceIsLoading,
      isFetching: toAccountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();

  const [getAllFromAccountBalance] = useLazyGetAllFromAccountBalancesQuery();

  const [getAllToAccountBalance] = useLazyGetAllToAccountBalancesQuery();

  const setCollapseAllRowsAction = () => {
    dispatch(setCollapseAllRows(true));
  };

  const onClearSidepanel = () => {
    dispatch(clearExchangeTransactionState());
    dispatch(clearState());

    dispatch(clearStatusesState());
    dispatch(clearSignaturesState());
  };

  const handleClose = useCallback(() => {
    dispatch(clearExchangeTransactionState());
    dispatch(clearState());
    dispatch(clearStatusesState());
    dispatch(clearSignaturesState());
    setUploadedFilesError([]);

    setTimeout(() => {
      if (backPath) {
        navigate(backPath);
      } else {
        navigate(
          withoutCorrections
            ? ROUTES.EXCHANGE_WITHOUT_CORRECTIONS
            : `${ROUTES.EXCHANGE}${search || ''}`,
        );
      }
    }, 100);

    setBackPath('');
  }, [backPath, search]);

  const {
    submitDisabled,
    calcError,
    isLoading,
    isUninitialized,
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    create,
    createSuccess,
    createInProgress,
    editInProgress,
    editIsError,
    calculateInProgress,
    calculateByIdInProgress,
    onSubmit,
    getTransaction,
    onClearTimer,
    internalError,
    onUpdateRates,
    getTransactionsList,
    getStatusesTransactionById,
    calculateTransactionRatesAction,
    calculateTransactionRatesByIdAction,
    calculateOnSwap,
    getSyncedTransactionsAction,
    syncedTransactionsIsLoading,
    syncedTransactionsIsFetching,
    getIntegrationLogs,
    integrationLogsIsLoading,
    integrationLogsIsFetching,
    transferDateWithTime,
    postingDateWithTime,
    exchangeDateWithTime,
    setTransferDateWithTime,
    setPostingDateWithTime,
    setExchangeDateWithTime,
  } = useRequests({
    setErrorMessage,
    setStatusChangeError,
    getValues,
    setDefaultCalculateResult,
    defaultCalculateResult,
    calculateResult,
    calculatedType,
    transactionId,
    setError,
    transaction,
    setCalculatedType,
    setValue,
    setCalculatedBodyValues,
    activePermissions,
    fields,
    exchangeType,
    prevExchangeActualRate,
    calculatedBodyValues,
    prevExchangeMarginPercent,
    prevExchangeDateOfRate,
    prevExchangeType,
    prevSellSum,
    prevBuySum,
    prevCurrencyFrom,
    prevCurrencyTo,
    rateSource,
    prevRateSource,
    clearErrors,
    prevSender,
    prevInversedRate,
    setInverseRateChanged,
    inverseRateChanged,
  });

  const {
    imgLink,
    deleteTransactionError,
    errorMessageSignature,
    setErrorMessageSignature,
    signaturePopupVisible,
    setSignaturePopupVisible,
    statusChangeInProgress,
    statusPopupVisible,
    setStatusPopupVisible,
    rejectPopupVisible,
    setRejectPopupVisible,
    deleteTransactionInProgress,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    editCommentInProgress,
    editCommentIsSuccess,
    deleteCommentInProgress,
    commentInProgress,
    documentCategoryChangeInProgress,
    documentStatusChangeInProgress,
    documentIsLoading,
    documentIsFetching,
    downloadInProgress,
    downloadIsFetching,
    downloadFileIsSuccess,
    addDocumentInProgress,
    fileUploadIsSuccess,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    glTransactionIsLoading,
    glTransactionIsFetching,
    glIsLoading,
    fileUploadInProgress,
    transactionChatsIsLoading,
    cofIsLoading,
    chatCreateInProgress,
    isLoadingSignatures,
    createSignatureInProgress,
    signatureIsSuccess,
    sendCodeInProgress,
    validateCodeInProgress,
    changeStatusIsSuccess,
    changeStatusIsLoading,
    isLoadingLogs,
    onDeleteTransaction,
    getTransactionDocumentyIdAction,
    onGlTransactionClick,
    onUploadDocument,
    onCreateTransactionChat,
    onSendCode,
    validateCodeAction,
    getLogsTransactionByIdAction,
    onClearRequestTimer,
    onChangeStatusPopup,
    onSignatureCancelClick,
    onChangeStatus,
    getGeneralLedgerTransactionValuesAction,
    getTransactionsChatsAction,
    getCompanyOfficialsAction,
    getSignaturesTransactionByIdAction,
    onReject,
    onDeleteDocument,
    onMoveDocument,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    onDeleteComment,
    onChangeCustomerDocumentsStatus,
    sendCodeRetry,
    sendCode,
    onClearRequestTimeout,
    getTransactionChangeLogsAction,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getTransactionCommentsAction,
    commentsIsLoading,
    commentsIsFetching,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    deleteCommentInTranscationIsSuccess,
  } = useBaseRequests({
    setError,
    transactionId,
    transaction,
    currentEmployee,
    sendMessage,
    setErrorMessage,
    createdTransactionId,
    statusUrl: StatusesUrl.EXCHANGE,
    setStatusChangeError,
    onSubmit,
    getTransactionsList,
    getStatusesTransactionById,
    onClearTimer,
    getTransaction,
    reset,
    handleClose,
    onClear: onClearSidepanel,
    chatTitle: 'RFI: Outgoing transaction',
    exchangeChatTitle: `RFI: Exchange transaction From ${formatBalance(
      Number(transaction?.exchangeCurrencyFixedSum) || 0,
    )} ${transaction?.exchangeCurrencyFrom?.name} To ${formatBalance(
      Number(transaction?.exchangeCurrencyFlexibleSum) || 0,
    )} ${transaction?.exchangeCurrencyTo?.name}  ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`,
    takeCount: selectedTake,
    search,
    transactionListUrl: ROUTES.EXCHANGE,
    errors,
    isDirty,
    documents,
    documentItem,
    setCollapseAllRows: setCollapseAllRowsAction,
    updateIsError: editIsError,
    expandedGLTransaction,
    setUploadedFilesError,
    transactionUrl: 'exchange',
  });

  const prevSignaturePopupVisible = usePrevious(signaturePopupVisible);

  const sendCodeAction = async () => {
    let createResponse;
    setErrorMessage('');

    if (!transactionId && !Object.keys(errors).length) {
      const values = getValues();

      const date = new Date(values.exchangeDateOfRate);
      const currentDate = new Date();
      const hours = currentDate.getHours();
      const minutes = currentDate.getMinutes();
      const seconds = currentDate.getSeconds();

      if (!exchangeDateWithTime) {
        date.setHours(hours);
        date.setMinutes(minutes);
        date.setSeconds(seconds);
      }

      const body: CreateExchangeTransactionFormValues = {
        sum:
          exchangeType === 'SELL'
            ? Number(values.sellSum)
            : Number(values.buySum),
        senderAccountNumber: values.senderAccountNumber,
        recipientAccountNumber: values.recipientAccountNumber,
        exchangeDateOfRate: exchangeDateWithTime
          ? date.toUTCString()
          : date.toISOString(),
        exchangeRateSourceId: values.exchangeRateSourceId,
        exchangeCurrencyFromId: values.exchangeCurrencyFromId,
        exchangeCurrencyToId: values.exchangeCurrencyToId,
        exchangeCurrencyFixedId:
          exchangeType === 'SELL'
            ? values.exchangeCurrencyFromId
            : values.exchangeCurrencyToId,
        exchangeMarginPercent:
          (calculateResult?.marginPercent as number) ||
          Number(values.exchangeMarginPercent),
        disableExternalTransactionCreate:
          values?.disableExternalTransactionCreate,
      };

      if (values.depositDateOfDestinationAccount) {
        const depositeDate = new Date(values.depositDateOfDestinationAccount);

        if (postingDateWithTime) {
          body.depositDateOfDestinationAccount = depositeDate?.toUTCString();
        } else {
          depositeDate.setHours(hours);
          depositeDate.setMinutes(minutes);
          depositeDate.setSeconds(seconds);
          body.depositDateOfDestinationAccount = depositeDate.toISOString();
        }
      }

      if (calculateResult?.quoteId) {
        body.quoteId = calculateResult.quoteId;
      }

      if (inverseRateChanged) {
        body.inverseActualRate =
          calculateResult?.inverseActualRate ||
          Number(values.inverseActualRate);
      } else {
        body.exchangeActualRate =
          calculateResult?.actualRate || Number(values.exchangeActualRate);
      }

      if (values?.blockchainTransactionHashId) {
        body.blockchainTransactionHashId = values?.blockchainTransactionHashId;
      }

      if (values.transferDate) {
        const transferDate = new Date(values.transferDate);

        if (transferDateWithTime) {
          body.transferDate = transferDate.toUTCString();
        } else {
          transferDate.setHours(hours);
          transferDate.setMinutes(minutes);
          transferDate.setSeconds(seconds);
          body.transferDate = transferDate.toISOString();
        }
      }

      if (values?.exchangeRateTolerance) {
        body.exchangeRateTolerance = Number(values.exchangeRateTolerance);
      }

      createResponse = (await create(body)) as {
        error: ResponseError;
        data?: {
          data: BaseTransaction;
        };
      };
    }

    if (createResponse && createResponse.error) {
      responseErrorsResolver({
        errors: createResponse.error,
        setError,
        setGlobalError: setErrorMessage,
      });
    } else if (createResponse && createResponse.data) {
      if (createResponse && createResponse.data) {
        setCreatedTransactionId(createResponse?.data?.data?.id);
      }

      if (currentEmployee?.canSignTransactions) {
        const response = (await sendCode({ mfa: MfaType.TOTP })) as {
          data: { data: MfaToken };
        };

        if (response && response.data && response.data.data) {
          setSignaturePopupVisible(true);

          const expireTime = Math.round(
            new Date(response.data.data.expiresAt).getTime() - Date.now(),
          );

          sendCodeRetry(expireTime);
        }
      }
    }
  };

  const [
    getSenderAccountsList,
    {
      isLoading: senderAccountsIsLoading,
      isFetching: senderAccountsIsUpdateInProgress,
    },
  ] = useLazyGetExchangeAccountsListQuery();

  const [
    getRecipientAccountsList,
    {
      isLoading: recipientAccountsIsLoading,
      isFetching: recipientAccountsIsUpdateInProgress,
    },
  ] = useLazyGetExchangeAccountsListQuery();

  const getSenderAccountsListAction = useCallback(async (query: string) => {
    if (query && query.length) {
      const response = await getSenderAccountsList(
        `?${query}&all=true&sortProp=fullNumber&sortDir=asc${
          transactionId ? '' : '&closed=false'
        }`,
      );

      if (response && response.data) {
        const filteredRes = response.data.data.edges
          .map((item) => item.node)
          .filter((item) => item.fullNumber !== sender);

        setSenderAccounts(filteredRes);
      }
    }
  }, []);

  const getRecipientAccountsListAction = useCallback(async (query: string) => {
    if (query && query.length) {
      const response = await getRecipientAccountsList(
        `?${query}&all=true&sortProp=fullNumber&sortDir=asc${
          transactionId ? '' : '&closed=false'
        }`,
      );

      if (response && response.data) {
        const filteredRes = response.data.data.edges
          .map((item) => item.node)
          .filter((item) => item.fullNumber !== recipient);

        setRecipientAccounts(filteredRes);
      }
    }
  }, []);

  const getAllFromAccountBalanceAction = useCallback((id: string) => {
    getAllFromAccountBalance({
      id,
    });
  }, []);

  const getExchangeAccountsCategoriesAction = useCallback(async () => {
    await getExchangeAccountsCategories('all=true&include=primaryAccounts');
  }, []);

  const getFromAccountBalanceAction = useCallback(async () => {
    const currentAccount = senderAccounts.find(
      (item) => item?.fullNumber === sender,
    );

    if (currentAccount) {
      const response = (await getFromAccountBalance({
        id: currentAccount?.id,
        currencyId: currencyFrom,
      })) as { data: { data: { edges: { node: { value: number } }[] } } };

      if (response?.data?.data) {
        setFromAccountBalance(response.data.data.edges[0]?.node.value);
      }
    }
  }, [getFromAccountBalance, currencyFrom, sender, senderAccounts]);

  const onSearch = async (name: string, searchText: string) => {
    const results: Account[] = [];

    if (searchText) {
      if (name.toLowerCase().indexOf('sender') >= 0) {
        const descriptionResponse = (await getSenderAccountsList(
          `?search=${searchText}${transactionId ? '' : '&closed=false'}`,
        )) as { data: Response<Account> };

        if (descriptionResponse?.data?.data) {
          descriptionResponse.data.data.edges?.forEach((item) => {
            results.push(item.node);
          });
        }

        const filteredRes = results;

        setSenderAccounts(filteredRes);
      } else if (name.toLowerCase().indexOf('recipient') >= 0) {
        const descriptionResponse = (await getRecipientAccountsList(
          `?search=${searchText}${transactionId ? '' : '&closed=false'}`,
        )) as { data: Response<Account> };

        if (descriptionResponse?.data?.data) {
          descriptionResponse.data.data.edges?.forEach((item) => {
            results.push(item.node);
          });
        }

        const filteredRes = results;

        setRecipientAccounts(filteredRes);
      }
    }
  };

  const getToAccountBalanceAction = useCallback(async () => {
    const currentAccount = recipientAccounts.find(
      (item) => item?.fullNumber === recipient,
    );

    if (currentAccount) {
      const response = (await getToAccountBalance({
        id: currentAccount?.id,
        currencyId: currencyTo,
      })) as { data: { data: { edges: { node: { value: number } }[] } } };

      if (response?.data?.data) {
        setToAccountBalance(response.data.data.edges[0]?.node.value);
      }
    }
  }, [recipientAccounts, currencyTo, recipient]);

  const getAllToAccountBalanceAction = useCallback((id: string) => {
    getAllToAccountBalance({
      id,
    });
  }, []);

  const onDuplicate = () => {
    onClearTimer(true);
    dispatch(clearExchangeTransactionState());
    dispatch(clearState());

    dispatch(clearStatusesState());
    dispatch(clearSignaturesState());
    setUploadedFilesError([]);
    onClearRequestTimer();
    reset({});

    setTimeout(() => {
      navigate(`${ROUTES.EXCHANGE}${search || ''}`);
    }, 100);

    setTimeout(() => {
      navigate(`${ROUTES.EXCHANGE_CREATE}${search || ''}`, {
        state: { ...transaction, duplicate: true },
      });
    }, 200);
  };

  const onAddMissing = async () => {
    addMissing(transactionId as string);
  };

  useEffect(() => {
    getExchangeAccountsCategoriesAction();

    if (transactionId) {
      dispatch(changePanelState({ state: true, transactionId }));
    } else {
      dispatch(changePanelState({ state: true }));
    }

    return () => {
      dispatch(clearExchangeTransactionState());
      dispatch(clearState());

      dispatch(clearStatusesState());
      dispatch(clearSignaturesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});
    };
  }, [dispatch, transactionId]);

  useEffect(() => {
    if (panelIsOpen && transactionId) {
      getTransaction();
    }
  }, [dispatch, panelIsOpen, transactionId, activePermissions]);

  useEffect(() => {
    if (errors.exchangeCurrencyFromId && currencyFrom) {
      clearErrors(['exchangeCurrencyFromId']);

      if (exchangeType === 'SELL') {
        clearErrors(['exchangeCurrencyFixedId']);
      }
    }
  }, [errors, currencyFrom, exchangeType]);

  useEffect(() => {
    if (errors.exchangeCurrencyToId && currencyTo) {
      clearErrors(['exchangeCurrencyToId']);

      if (exchangeType === 'BUY') {
        clearErrors(['exchangeCurrencyFixedId']);
      }
    }
  }, [errors, currencyFrom, exchangeType]);

  useEffect(() => {
    if (transaction) {
      reset({
        createdAt: transaction?.createdAt,
        exchangeDateOfRate: transaction.exchangeDateOfRate,
        exchangeRateSourceId: transaction.exchangeRateSourceId,
        exchangeCurrencyFromId: transaction.exchangeCurrencyFromId,
        exchangeCurrencyToId: transaction.exchangeCurrencyToId,
        exchangeCurrencyFixedId: transaction.exchangeCurrencyFixedId,
        exchangeMarginPercent: transaction.exchangeMarginPercent,
        exchangeActualRate: transaction.exchangeActualRate,
        transferDate: transaction.transferDate,
        sum: transaction?.sum?.toString(),
        senderAccountNumber: transaction.senderAccountNumber,
        recipientAccountNumber: transaction.recipientAccountNumber,
        exchangeType:
          transaction.exchangeCurrencyFixedId ===
          transaction.exchangeCurrencyFromId
            ? 'SELL'
            : 'BUY',
        calculatedAmount: transaction?.exchangeCurrencyFlexibleSum?.toString(),
        exchangeProfit: transaction.exchangeProfit,
        baseRate: transaction.exchangeBaseRate?.toString(),
        providersRate: transaction?.exchangeRate?.toString(),
        depositDateOfDestinationAccount:
          transaction?.depositDateOfDestinationAccount,
        blockchainTransactionHashId: transaction?.blockchainTransactionHashId,
        exchangeRateTolerance: transaction?.exchangeRateTolerance?.toString(),
        disableExternalTransactionCreate:
          transaction?.disableExternalTransactionCreate,
      });

      setDefaultCalculateResult({
        exchangeActualRate: transaction?.exchangeActualRate,
        exchangeMarginPercent: transaction?.exchangeMarginPercent,
        exchangeDateOfRate: transaction?.exchangeDateOfRate,
        inverseActualRate: transaction?.exchangeInverseActualRate,
      });

      dispatch(
        setCalculatedResult({
          fixedAmount: transaction.sum,
          calculatedAmount: transaction.exchangeCurrencyFlexibleSum as number,
          rate: transaction.exchangeRate as number,
          baseRate: transaction?.exchangeBaseRate,
          actualRate: transaction.exchangeActualRate as number,
          marginPercent: transaction.exchangeMarginPercent as number,
          profit: transaction.exchangeProfit as number,
          profitRate: transaction?.exchangeProfitRate as number,
          inverseActualRate: transaction?.exchangeInverseActualRate,
          inverseBaseRate: transaction?.exchangeInverseBaseRate,
          inverseRate: transaction?.exchangeInverseRate,
        }),
      );
    }
  }, [transaction, accountCategories]);

  useEffect(() => {
    if (prevSender && !sender) {
      setValue('recipientAccountNumber', '');
      setRecipientAccounts([]);

      if (errors?.senderAccountNumber?.message) {
        clearErrors(['senderAccountNumber']);
      }
    }
  }, [sender, prevSender]);

  useEffect(() => {
    if (prevRecipient && !recipient) {
      if (errors?.recipientAccountNumber?.message) {
        clearErrors(['recipientAccountNumber']);
      }
    }
  }, [recipient, prevRecipient]);

  useEffect(() => {
    if (
      sender &&
      !transactionId &&
      !locationState &&
      !currencyTo &&
      !recipient
    ) {
      setValue('recipientAccountNumber', sender);
    }
  }, [sender, transactionId, locationState, currencyTo, recipient]);

  useEffect(() => {
    if (
      (recipient || (recipient && recipient !== prevRecipient)) &&
      (toAccountBalances ||
        (prevToAccountBalances &&
          !isEqual(prevToAccountBalances, toAccountBalances))) &&
      currencyTo
    ) {
      if (!toAccountBalances?.find((item) => item.currencyId === currencyTo)) {
        setError('recipientAccountNumber', {
          type: 'custom',
          message: 'The selected currency is not available for this account.',
        });
      } else {
        clearErrors(['recipientAccountNumber']);
      }
    }
  }, [
    recipient,
    toAccountBalances,
    currencyTo,
    prevRecipient,
    prevToAccountBalances,
  ]);

  useEffect(() => {
    if (
      (sender || (sender && sender !== prevSender)) &&
      (fromAccountBalances ||
        (fromAccountBalances &&
          !isEqual(prevFromAccountBalances, fromAccountBalances))) &&
      currencyFrom
    ) {
      if (
        !fromAccountBalances?.find((item) => item.currencyId === currencyFrom)
      ) {
        setError('senderAccountNumber', {
          type: 'custom',
          message: 'The selected currency is not available for this account.',
        });
      } else {
        clearErrors(['senderAccountNumber']);
      }
    }
  }, [
    sender,
    fromAccountBalances,
    currencyFrom,
    prevSender,
    prevFromAccountBalances,
  ]);

  useEffect(() => {
    if (
      sender &&
      prevSender &&
      sender !== prevSender &&
      !transactionId &&
      !locationState &&
      currencyTo &&
      fromAccountBalances &&
      prevFromAccountBalances &&
      !isEqual(fromAccountBalance, prevFromAccountBalances)
    ) {
      if (fromAccountBalances?.find((item) => item.currencyId === currencyTo)) {
        setValue('recipientAccountNumber', sender);
      }
    }
  }, [
    sender,
    transactionId,
    locationState,
    currencyTo,
    senderAccounts,
    prevFromAccountBalances,
  ]);

  useEffect(() => {
    if (sender && senderAccounts) {
      const currentAccount = senderAccounts.find(
        (item) => item?.fullNumber === sender,
      );

      if (currentAccount) {
        getAllFromAccountBalanceAction(currentAccount.id);
      }
    }
  }, [sender, senderAccounts]);

  useEffect(() => {
    if (recipient && recipientAccounts) {
      const currentAccount = recipientAccounts.find(
        (item) => item?.fullNumber === recipient,
      );

      if (currentAccount) {
        getAllToAccountBalanceAction(currentAccount.id);
      }
    }
  }, [recipient, recipientAccounts]);

  useEffect(() => {
    if (sender && currencyFrom && senderAccounts) {
      getFromAccountBalanceAction();
    }
  }, [currencyFrom, sender, senderAccounts]);

  useEffect(() => {
    if (recipient && currencyTo && recipientAccounts) {
      getToAccountBalanceAction();
    }
  }, [currencyTo, recipient, recipientAccounts]);

  useEffect(() => {
    if (
      changeStatusIsSuccess &&
      (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
        transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
        transaction?.baseellaStatus === BaseellaStatus.REJECTED)
    ) {
      getTransactionsList(
        `?take=${selectedTake || '25'}${search.replace('?', '&')}`,
      );
    }

    if (
      (signatureIsSuccess && !transactionId) ||
      (changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED))
    ) {
      dispatch(clearExchangeTransactionState());
      dispatch(clearState());

      dispatch(clearStatusesState());
      dispatch(clearSignaturesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});

      handleClose();

      if (
        changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED)
      ) {
        setTimeout(() => {
          getTransactionsList(
            `?take=${selectedTake || '25'}${search.replace('?', '&')}`,
          );
        }, 100);
      }
    }
  }, [signatureIsSuccess, transactionId]);

  useEffect(() => {
    if (signatureIsSuccess && transactionId) {
      onClearTimer(true);
      onClearRequestTimeout();
      setSignaturePopupVisible(false);

      setTimeout(() => {
        getTransactionsList(
          `?take=${selectedTake || '25'}${search.replace('?', '&')}`,
        );
      }, 100);

      getTransaction();
      getSignaturesTransactionByIdAction();
    }
  }, [signatureIsSuccess, transactionId]);

  useEffect(() => {
    if (locationState && locationState.duplicate) {
      if (!accountCategories) {
        setTimeout(() => {
          getExchangeAccountsCategoriesAction();
        }, 500);
      }

      if (!fields.senderAccountNumber) {
        onSearch('senderAccountNumber', locationState.senderAccountNumber);
      }

      if (!fields.recipientAccountNumber) {
        onSearch(
          'recipientAccountNumber',
          locationState.recipientAccountNumber,
        );
      }

      const type =
        locationState.exchangeCurrencyFixedId ===
        locationState.exchangeCurrencyFromId
          ? 'SELL'
          : 'BUY';

      reset({
        transferDate: new Date().toISOString(),
        exchangeDateOfRate: new Date().toISOString(),
        senderAccountNumber: locationState.senderAccountNumber,
        recipientAccountNumber: locationState.recipientAccountNumber,
        exchangeType:
          locationState.exchangeCurrencyFixedId ===
          locationState.exchangeCurrencyFromId
            ? 'SELL'
            : 'BUY',
        exchangeCurrencyFromId: locationState.exchangeCurrencyFromId,
        sum: locationState.sum,
        sellSum: type === 'SELL' ? locationState.sum : '',
        buySum: type === 'BUY' ? locationState.sum : '',
        exchangeCurrencyToId: locationState.exchangeCurrencyToId,
        calculatedAmount: locationState.exchangeCurrencyFlexibleSum,
        exchangeMarginPercent: locationState?.exchangeMarginPercent,
        exchangeCurrencyFixedId:
          locationState.exchangeCurrencyFixedId ===
          locationState.exchangeCurrencyFromId
            ? locationState.exchangeCurrencyFromId
            : locationState.exchangeCurrencyToId,
        exchangeRateSourceId: locationState.exchangeRateSourceId || '',
        exchangeRateTolerance: locationState?.exchangeRateTolerance || '',
      });

      calculateTransactionRatesAction({
        exchangeDateOfRate: locationState.exchangeDateOfRate,
        exchangeRateSourceId: locationState.exchangeRateSourceId,
        exchangeCurrencyFromId: locationState.exchangeCurrencyFromId,
        exchangeCurrencyToId: locationState.exchangeCurrencyToId,
        exchangeCurrencyFixedId:
          locationState.exchangeCurrencyFixedId ===
          locationState.exchangeCurrencyFromId
            ? locationState.exchangeCurrencyFromId
            : locationState.exchangeCurrencyToId,
        sum: locationState.sum,
        senderAccountNumber: locationState.senderAccountNumber,
        recipientAccountNumber: locationState.recipientAccountNumber,
      });
    }
  }, [locationState]);

  useEffect(() => {
    if (transactionId && isDirty) {
      onClearTimer();
    }
  }, [isDirty, transactionId]);

  useEffect(() => {
    if (transferDateValue) {
      const transferDateFormatted = formatFormDate(transferDateValue);
      const currentDate = formatFormDate(new Date().toISOString());

      if (transferDateValue && transferDateFormatted > currentDate) {
        setError('transferDate', {
          type: 'custom',
          message: 'Creation Date cannot be later than current date',
        });
      } else {
        clearErrors('transferDate');
      }
    }
  }, [transferDateValue]);

  useEffect(() => {
    if (!prevSignaturePopupVisible && errorMessageSignature) {
      setErrorMessageSignature('');
    }
  }, [prevSignaturePopupVisible, errorMessageSignature]);

  const isLoadingSidepanel = useMemo(() => {
    let res = true;

    let isLoadingSidepanelVariables =
      !isLoading &&
      !isUninitialized &&
      !fromAccountBalanceIsLoading &&
      !toAccountBalanceIsLoading;

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_EXCHANGE_EDIT_ACTION,
      )
    ) {
      isLoadingSidepanelVariables =
        isLoadingSidepanelVariables &&
        !isUninitializedStatuses &&
        !isLoadingStatuses;
    }

    if (transactionId) {
      if (isLoadingSidepanelVariables) {
        res = false;
      }
    } else {
      res = false;
    }

    return res;
  }, [
    transactionId,
    isLoading,
    isLoadingStatuses,
    isUninitialized,
    isUninitializedStatuses,
    activePermissions,
  ]);

  useEffect(() => {
    if (
      baseRateSettings &&
      !transactionId &&
      !locationState &&
      !locationState?.duplicate
    ) {
      setValue('exchangeRateSourceId', baseRateSettings?.exchangeRateSourceId);
    }
  }, [baseRateSettings, transactionId, locationState]);

  useEffect(() => {
    if (locationState && !locationState.duplicate) {
      setBackPath(locationState);
    }
  }, [locationState]);

  useEffect(() => {
    if (
      currentEmployee &&
      !currentEmployee.canSignTransactions &&
      createSuccess
    ) {
      handleClose();
    }
  }, [currentEmployee, createSuccess]);

  useEffect(() => {
    if (
      calculateResult &&
      !transactionId &&
      prevCalculatedAmount !== calculateResult.calculatedAmount
    ) {
      if (exchangeType === 'SELL') {
        setValue('buySum', calculateResult.calculatedAmount);
      } else if (exchangeType === 'BUY') {
        setValue('sellSum', calculateResult.calculatedAmount);
      }
    }
  }, [calculateResult, exchangeType, transactionId, prevCalculatedAmount]);

  const onSendCodeAction = !transactionId ? sendCodeAction : onSendCode;

  return (
    <ExchangeSidepanel
      panelIsOpen={panelIsOpen}
      sidepanelExpandedKey="exchange-transaction-sidepanel"
      handleClose={handleClose}
      control={control as Control<any>}
      isLoading={isLoadingSidepanel || Boolean(!transaction && transactionId)}
      statusesInfo={transactionId ? statuses : undefined}
      onRollbackStatus={
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_EXCHANGE_REVERSE_ACTION,
        )
          ? (status) => onChangeStatus(status)
          : undefined
      }
      onNextStatus={
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_EXCHANGE_EDIT_ACTION,
        )
          ? (status) => onChangeStatus(status)
          : undefined
      }
      internalError={internalError}
      onChangeStatusPopup={onChangeStatusPopup}
      setStatusPopupVisible={setStatusPopupVisible}
      statusPopupVisible={statusPopupVisible}
      transactionInfo={transactionId ? transaction : undefined}
      recipientAccounts={recipientAccounts}
      senderAccounts={senderAccounts}
      senderAccountsIsLoading={
        senderAccountsIsLoading || senderAccountsIsUpdateInProgress
      }
      recipientAccountsIsLoading={
        recipientAccountsIsLoading || recipientAccountsIsUpdateInProgress
      }
      currencies={currencies}
      isEditable={
        transactionId
          ? activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_EXCHANGE_EDIT_ACTION,
            )
          : activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_EXCHANGE_CREATE_ACTION,
            )
      }
      createAllowed={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_EXCHANGE_CREATE_ACTION,
      )}
      logs={statusLogs}
      isCreateForm={!transactionId}
      rateSources={rateSources}
      title={transactionId ? 'Exchange' : 'New Exchange'}
      isDisabledSaveButton={
        !isDirty ||
        createInProgress ||
        editInProgress ||
        calculateInProgress ||
        calculateByIdInProgress ||
        sendCodeInProgress ||
        submitDisabled ||
        Boolean(calcError) ||
        Boolean(Object.keys(errors)?.length)
      }
      createInProgress={createInProgress}
      currencyFromId={currencyFrom}
      currencyToId={currencyTo}
      fromAccountBalance={fromAccountBalance}
      balanceFromAccountIsLoading={
        fromAccountBalanceIsLoading || fromAccountBalanceIsFetching
      }
      toAccountBalance={toAccountBalance}
      balanceToAccountIsLoading={
        toAccountBalanceIsLoading || toAccountBalanceIsFetching
      }
      errors={errors && Object.keys(errors).length ? errors : undefined}
      exchangeType={exchangeType}
      setValue={setValue}
      senderAccountNumber={sender}
      calculateResult={calculateResult}
      baseellaStatus={transaction?.baseellaStatus}
      changeLogs={transactionChangeLogs}
      exchangeCurrencyFixedId={exchangeCurrencyFixedId}
      isDirty={isDirty}
      calculateInProgress={calculateInProgress || calculateByIdInProgress}
      calcError={calcError}
      accountCategories={accountCategories}
      accountsCategoriesLoading={accountsCategoriesLoading}
      onSearchAutocomplete={onSearch}
      setSenderAccounts={setSenderAccounts}
      setSenderAccountQuery={getSenderAccountsListAction}
      setRecipientAccounts={setRecipientAccounts}
      setRecipientAccountQuery={getRecipientAccountsListAction}
      signaturePopupVisible={signaturePopupVisible}
      sendCode={onSendCodeAction}
      validateCodeInProgress={
        validateCodeInProgress ||
        sendCodeInProgress ||
        createSignatureInProgress
      }
      validateCode={validateCodeAction}
      handleSubmit={onSubmit}
      transactionId={transactionId}
      signatures={signatures}
      enableEdit
      errorSidepanel={errorMessage}
      rejectPopupVisible={rejectPopupVisible}
      setRejectPopupVisible={setRejectPopupVisible}
      onReject={onReject}
      rejectInProgress={changeStatusIsLoading}
      errorMessageSignature={errorMessageSignature}
      updateStatusIsLoading={statusChangeInProgress}
      isLoadingStatuses={isFetchingStatuses}
      onSignatureCancelClick={onSignatureCancelClick}
      latestQueuedStatus={transaction?.latestQueuedStatuses?.baseellaStatus}
      statusChangeError={statusChangeError}
      createdByOrganizationId={transaction?.createdByOrganizationId}
      onChangeStatusPopupLoading={editInProgress || changeStatusIsLoading}
      editInProgress={editInProgress}
      companyOfficials={companyOfficials}
      chats={chats}
      onCreateTransactionChat={onCreateTransactionChat}
      chatCreateInProgress={chatCreateInProgress}
      currentEmployee={currentEmployee}
      documents={documents}
      document={documentItem}
      documentFileIsLoading={downloadInProgress || downloadIsFetching}
      downloadInProgress={downloadInProgress || downloadIsFetching}
      imgLink={imgLink}
      onDelete={onDeleteDocument}
      deleteInProgress={deleteDocumentInProgress}
      deleteIsSuccess={deleteDocumentIsSuccess}
      onMove={onMoveDocument}
      moveInProgress={documentCategoryChangeInProgress}
      onDownload={onDownloadDocument}
      sendComment={onSendDocumentComment}
      editComment={onEditComment}
      editCommentInProgress={editCommentInProgress}
      editCommentIsSuccess={editCommentIsSuccess}
      deleteComment={onDeleteComment}
      deleteCommentInProgress={deleteCommentInProgress}
      commentInProgress={commentInProgress}
      statusChangeInProgress={documentStatusChangeInProgress}
      changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
      uploadDocumentErrors={uploadedFilesError}
      uploadDocumentInProgress={fileUploadInProgress || addDocumentInProgress}
      uploadDocumentIsSuccess={fileUploadIsSuccess}
      onUploadDocument={onUploadDocument}
      onDownloadSuccess={downloadFileIsSuccess}
      getTransactionDocument={getTransactionDocumentyIdAction}
      documentIsLoading={documentIsLoading || documentIsFetching}
      glTransactionsIsLoading={glIsLoading}
      glTransactionIsLoading={glTransactionIsLoading || glTransactionIsFetching}
      glTransactions={glTransactions}
      onGlTransactionClick={onGlTransactionClick}
      glTransaction={expandedGLTransaction}
      clearGlTransactionState={() =>
        dispatch(clearExpandedGeneralLedgerTransactionState())
      }
      expandedGLTransactionCreditBalance={expandedGLTransactionCreditBalance}
      expandedGLTransactionDebitBalance={expandedGLTransactionDebitBalance}
      expendedDebitBalanceIsLoading={
        debitBalanceIsLoading || debitBalanceIsFetching
      }
      expendedCreditBalanceIsLoading={
        creditBalanceIsLoading || creditBalanceIsFetching
      }
      deleteIsVisible={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_DOCUMENTS_DELETE_ACTION,
      )}
      deleteTransactionError={deleteTransactionError}
      onDeleteTransactions={onDeleteTransaction}
      deleteTransactionInProgress={deleteTransactionInProgress}
      canSignTransaction={currentEmployee?.canSignTransactions}
      deleteTransactionDisabled={
        !activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DELETE_ACTION,
        )
      }
      onUpdateRates={onUpdateRates}
      providersRate={providersRate}
      sendCodeInProgress={sendCodeInProgress}
      fromAccountBalances={fromAccountBalances}
      toAccountBalances={toAccountBalances}
      currenciesList={currenciesList}
      onDuplicate={onDuplicate}
      duplicate={locationState?.duplicate}
      calculateTransactionRatesByIdAction={calculateTransactionRatesByIdAction}
      fields={fields}
      activePermission={activePermissions}
      caspEnabled={
        paymentTypes?.find(
          (item) => item.paymentType === 'CRYPTO_ASSET_PAYMENT',
        )?.enabled
      }
      transactionChatsIsLoading={transactionChatsIsLoading || cofIsLoading}
      isLoadingSignatures={isLoadingSignatures}
      isLoadingLogs={isLoadingLogs}
      getLogsTransactionById={getLogsTransactionByIdAction}
      getGeneralLedgerTransactionValues={
        getGeneralLedgerTransactionValuesAction
      }
      getTransactionsChats={getTransactionsChatsAction}
      getCompanyOfficials={getCompanyOfficialsAction}
      getSignaturesTransactionById={getSignaturesTransactionByIdAction}
      overdraftEnabled={overdraftEnabled}
      baseRateSource={baseRateSettings?.baseRateSourceId}
      calculateOnSwap={calculateOnSwap}
      getTransactionChangeLogs={getTransactionChangeLogsAction}
      changeLogsIsLoading={changeLogsIsLoading}
      users={users}
      transactionChangeLogsPagination={transactionChangeLogsPagination}
      changeLogsIsFetching={changeLogsIsFetching}
      setInverseRateChanged={setInverseRateChanged}
      exchangeDateOfRate={exchangeDateOfRate}
      transactionComments={transactionComments}
      transactionCommentsPagination={transactionCommentsPagination}
      getTransactionCommentsAction={getTransactionCommentsAction}
      commentsIsLoading={commentsIsLoading}
      commentsIsFetching={commentsIsFetching}
      onAddCommentToTransaction={onAddCommentToTransaction}
      addCommentInProgress={addCommentInProgress}
      addCommentIsSuccess={addCommentIsSuccess}
      onEditCommentInTransaction={onEditCommentInTransaction}
      editTransactionCommentInProgress={editTransactionCommentInProgress}
      editTrancastionCommentIsSuccess={editTrancastionCommentIsSuccess}
      onDeleteCommentInTransaction={onDeleteCommentInTransaction}
      deleteCommentInTransactionInProgress={
        deleteCommentInTransactionInProgress
      }
      deleteCommentInTranscationIsSuccess={deleteCommentInTranscationIsSuccess}
      syncedTransactionsIds={transaction?.children?.map((item) => item.id)}
      getSyncedTransactions={getSyncedTransactionsAction}
      syncedTransactionsIsLoading={syncedTransactionsIsLoading}
      syncedTransactionsIsFetching={syncedTransactionsIsFetching}
      syncedTransactions={syncedTransactions}
      getIntegrationLogs={getIntegrationLogs}
      integrationLogsIsLoading={integrationLogsIsLoading}
      integrationLogsIsFetching={integrationLogsIsFetching}
      integrationLogs={integrationLogs}
      integrationLogsPagination={integrationLogsPagination}
      withoutCorrections={withoutCorrections}
      onAddMissing={onAddMissing}
      addMissingInProgress={addMissingInProgress}
      deleteDisabled={ffSettings?.ffDisableTxGlRemoval}
      setError={setError}
      clearErrors={clearErrors}
      withTime={{
        transferDateWithTime,
        postingDateWithTime,
        exchangeDateWithTime,
      }}
      setWithTime={{
        setTransferDateWithTime,
        setPostingDateWithTime,
        setExchangeDateWithTime,
      }}
    />
  );
};

export default ExchangeTransaction;
